import React, { useState, useEffect } from 'react';
import { Cloud, Server, Shield, Zap, CheckCircle, ArrowRight, Users, TrendingUp, Lock, Rocket, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Footer } from '../components/Constants';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: "/img/dashboard.png",
      title: "Global Infrastructure",
      description: "Deploy across 15+ regions worldwide",
      link: "/admin/login",
      linkText: "Get Started"
    },
    {
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=600&fit=crop",
      title: "Enterprise Security",
      description: "Bank-grade encryption and compliance",
      link: "/contact",
      linkText: "Learn More"
    },
    {
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop",
      title: "Real-time Analytics",
      description: "Monitor your applications 24/7",
      link: "/admin/login",
      linkText: "Try It Free"
    },
    {
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop",
      title: "Scale with Confidence",
      description: "Auto-scaling infrastructure on demand",
      link: "/contact",
      linkText: "Contact Sales"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Cloud & On-Premise",
      description: "Deploy your applications in the cloud or on your own infrastructure with complete flexibility."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Microservices Ready",
      description: "Built for modern microservice architectures with container orchestration and scaling."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Enterprise Security",
      description: "Bank-grade security with automated SSL, DDoS protection, and compliance management."
    },
    {
      icon: <Server className="w-8 h-8" />,
      title: "Managed Infrastructure",
      description: "We handle all DevOps so you can focus on building your product, not managing servers."
    }
  ];

  const services = [
    "Application Deployment & Hosting",
    "DevOps Consulting & Setup",
    "Security Audits & Compliance",
    "24/7 Monitoring & Support",
    "CI/CD Pipeline Configuration",
    "Custom Infrastructure Design"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-400">Enterprise-Grade DevOps for Everyone</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Deploy with
                <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Confidence
                </span>
              </h1>
              
              <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                Professional hosting and microservices deployment for startups and small businesses. 
                No DevOps team required.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/admin/login"
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl hover:shadow-blue-500/50 transition-all flex items-center justify-center space-x-2 group"
                >
                  <Rocket className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  <span>Get Started Free</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/contact"
                  className="border border-slate-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 hover:border-slate-600 transition-all text-center"
                >
                  Talk to Sales
                </Link>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-6">
                <div className="group hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">99.9%</div>
                  <div className="text-sm text-slate-400">Uptime SLA</div>
                </div>
                <div className="group hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">24/7</div>
                  <div className="text-sm text-slate-400">Support</div>
                </div>
                <div className="group hover:scale-105 transition-transform">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">500+</div>
                  <div className="text-sm text-slate-400">Deployments</div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="mt-8 flex items-center space-x-4 text-sm text-slate-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Free 14-day trial</span>
                <span>•</span>
                <span>No credit card required</span>
                <span>•</span>
                <span>Cancel anytime</span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 blur-3xl"></div>
              <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-800 p-8 shadow-2xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-blue-500/50 transition-colors">
                    <span className="text-sm font-medium">Application Status</span>
                    <span className="flex items-center text-green-400 text-sm font-semibold">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                      Running
                    </span>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-blue-500/50 transition-colors">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-xs text-slate-400 font-medium">CPU Usage</div>
                      <div className="text-sm text-blue-400 font-semibold">45%</div>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-500 to-cyan-400 h-2 rounded-full animate-pulse" style={{width: '45%'}}></div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-blue-500/50 transition-colors">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-xs text-slate-400 font-medium">Memory Usage</div>
                      <div className="text-sm text-cyan-400 font-semibold">62%</div>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full animate-pulse" style={{width: '62%'}}></div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-blue-400">12ms</div>
                        <div className="text-xs text-slate-400">Response Time</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-400">1.2k</div>
                        <div className="text-xs text-slate-400">Requests/min</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Slider Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative">
            {/* Slider Container */}
            <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden group">
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {/* Background Image with Overlay */}
                  <div className="absolute inset-0">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-transparent"></div>
                  </div>

                  {/* Content */}
                  <div className="relative h-full flex items-center px-8 lg:px-16">
                    <div className="max-w-2xl">
                      <h3 className="text-4xl lg:text-5xl font-bold mb-4 text-white">
                        {slide.title}
                      </h3>
                      <p className="text-xl text-slate-300 mb-8">
                        {slide.description}
                      </p>
                      <Link
                        to={slide.link}
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl hover:shadow-blue-500/50 transition-all group"
                      >
                        <span>{slide.linkText}</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-800 hover:border-blue-500/50 transition-all opacity-0 group-hover:opacity-100"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-800 hover:border-blue-500/50 transition-all opacity-0 group-hover:opacity-100"
                aria-label="Next slide"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Dot Indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide
                        ? 'bg-blue-400 w-8'
                        : 'bg-slate-500 hover:bg-slate-400'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Built for Modern Development</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">Everything you need to deploy and scale your applications with confidence</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-blue-500/50 hover:bg-slate-900/70 transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/10 to-cyan-400/10 rounded-lg flex items-center justify-center mb-4 text-blue-400 group-hover:scale-110 group-hover:from-blue-500/20 group-hover:to-cyan-400/20 transition-all">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-blue-400 transition-colors">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Comprehensive DevOps Solutions</h2>
              <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                From deployment to monitoring, we handle the technical complexity so you can focus on growing your business.
              </p>
              
              <div className="space-y-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3 group hover:translate-x-2 transition-transform">
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 group-hover:scale-110 transition-transform" />
                    <span className="text-lg text-slate-300 group-hover:text-white transition-colors">{service}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link 
                  to="/admin/login"
                  className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors group"
                >
                  <span className="font-semibold">Start your free trial</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-slate-800 rounded-xl p-6 hover:border-blue-500/50 hover:from-blue-500/20 transition-all">
                <Users className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Startups</h3>
                <p className="text-slate-400">Deploy fast without hiring a DevOps team</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-400/10 to-transparent border border-slate-800 rounded-xl p-6 mt-8 hover:border-cyan-400/50 hover:from-cyan-400/20 transition-all">
                <TrendingUp className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Scale</h3>
                <p className="text-slate-400">Grow from 10 to 10,000 users seamlessly</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-slate-800 rounded-xl p-6 -mt-8 hover:border-purple-500/50 hover:from-purple-500/20 transition-all">
                <Lock className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Secure</h3>
                <p className="text-slate-400">Enterprise security by default</p>
              </div>
              <div className="bg-gradient-to-br from-green-500/10 to-transparent border border-slate-800 rounded-xl p-6 hover:border-green-500/50 hover:from-green-500/20 transition-all">
                <Shield className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Compliance</h3>
                <p className="text-slate-400">Meet regulatory requirements</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-400/10 border border-slate-800 rounded-2xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-cyan-400/5"></div>
            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-xl text-slate-400 mb-8">
                Join hundreds of companies deploying with confidence. Start your free trial today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/admin/login"
                  className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl hover:shadow-blue-500/50 transition-all group"
                >
                  <Rocket className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
                  <span>Start Free Trial</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center space-x-2 border border-slate-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 hover:border-slate-600 transition-all"
                >
                  <span>Contact Sales</span>
                </Link>
              </div>
              <p className="mt-6 text-sm text-slate-500">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Using component from constants */}
      <Footer />
    </div>
  );
};

export default HomePage;